// Ported from design/project/App76-Chart.dc.html, then wired: live candles from our battle price, and LONG / SHORT
// straight into the search. The top and the stats scroll on a short phone; LONG and SHORT stay at the bottom.
import { useEffect, useState } from "react";
import { CandleChart, chartArena, saveTimeframe, savedTimeframe, TIMEFRAMES, useArenaStats, type Timeframe } from "../chart.js";
import { fit } from "../fit.js";
import { fmtPrice, lastPrice } from "../format.js";
import { payoutFor, useGame, type Side } from "../game.js";
import { go } from "../router.js";
import { play } from "../sound.js";

const ERRORS: Record<string, string> = {
  low_balance: "Not enough balance for this stake.",
  already_in_battle: "You are already in a battle.",
  already_queued: "You are already searching for an opponent.",
  arena_closed: "This arena is closed right now.",
  stake_not_allowed: "That stake is above your limit.",
  network: "No connection. Check your data and try again.",
};
const COIN: Record<string, string> = { BTC: "#F7931A", ETH: "#627EEA", SOL: "#14F195", TON: "#0098EA", XAU: "#E6B800" };
export const DECIMALS: Record<string, number> = { BTC: 2, ETH: 2, SOL: 2, TON: 4, XAU: 2 };

export function Tabs({ tf, set, compact }: { tf: Timeframe; set: (tf: Timeframe) => void; compact?: boolean }) {
  return (
    <div role="tablist" style={compact
      ? { display: "flex", gap: "2px", padding: "3px", borderRadius: "12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }
      : { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "4px", padding: "4px", borderRadius: "13px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
      {TIMEFRAMES.map((t) => (
        <button key={t.id} role="tab" aria-selected={tf === t.id} onClick={() => set(t.id)}
          style={{ height: compact ? "28px" : "32px", padding: compact ? "0 10px" : "0", border: "0", borderRadius: compact ? "9px" : "10px", fontFamily: "Inter, sans-serif", fontSize: "12px", cursor: "pointer",
            ...(tf === t.id ? { background: "#CCFF00", color: "#0B0E14", fontWeight: "700" } : { background: "transparent", color: "#8B949E", fontWeight: "600" }) }}>{t.label}</button>
      ))}
    </div>
  );
}

export function CallButtons({ height, column, onPick, disabled }: { height: string; column?: boolean; onPick: (s: Side) => void; disabled: boolean }) {
  const base = { height, fontSize: "15px", whiteSpace: "nowrap", gap: "8px", border: "0", cursor: "pointer", opacity: disabled ? 0.5 : 1 } as const;
  return (
    <div style={{ display: "grid", gridTemplateColumns: column ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: "10px" }}>
      <button disabled={disabled} onClick={() => onPick("long")} className="btnP" style={base} aria-label="Long: price goes up">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="#0B0E14" aria-hidden="true"><path d="M12 4 L21 15 H3 Z"></path></svg>LONG</button>
      <button disabled={disabled} onClick={() => onPick("short")} className="btnP" style={{ ...base, background: "#FF3366", color: "#FFFFFF", boxShadow: "0 0 24px rgba(255,51,102,0.35)" }} aria-label="Short: price goes down">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="#FFFFFF" aria-hidden="true"><path d="M12 20 L21 9 H3 Z"></path></svg>SHORT</button>
    </div>
  );
}

/** LONG or SHORT from a chart: the side is picked, so the search starts at once. */
export function usePick() {
  const { findOpponent, error, clearError, topUp } = useGame();
  const [busy, setBusy] = useState(false);
  // A message belongs to this visit: it goes when the player leaves.
  useEffect(() => () => clearError(), []);
  const pick = async (side: Side) => {
    if (busy) return;
    play("tap");
    setBusy(true);
    try {
      await findOpponent(side, {}, "App76");
    } catch {
      setBusy(false);
    }
  };
  return { pick, busy, error, topUp };
}

/** In place of LONG / SHORT on an arena that isn't open yet: the chart is there to watch, battles come later. */
export function ComingSoon() {
  return (
    <div style={{ height: "56px", borderRadius: "16px", border: "1px dashed rgba(255,255,255,0.18)", background: "rgba(22,27,34,0.55)", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontSize: "13px" }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8B949E" strokeWidth="2.4" strokeLinecap="round" style={{ display: "block" }} aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"></rect><path d="M8 11V7a4 4 0 0 1 8 0v4"></path></svg>
      <span><b>Coming soon</b> <span className="muted">· opens as BTC gets busy</span></span>
    </div>
  );
}

export function App76_Chart() {
  const { selection, prices, arenas, feeNow } = useGame();
  const arena = chartArena(selection.arena);
  const [tf, setTfState] = useState<Timeframe>(savedTimeframe);
  const setTf = (t: Timeframe) => {
    saveTimeframe(t);
    setTfState(t);
  };
  const { pick, busy, error, topUp } = usePick();
  const stats = useArenaStats(arena, selection.durationSec);
  const live = lastPrice(prices[arena]);
  const change = stats && live ? ((live - stats.first) / stats.first) * 100 : null;
  const open = arenas.find((a) => a.id === arena)?.open ?? false;
  const minutes = selection.durationSec / 60;
  const pair = arena === "XAU" ? "GOLD XAU/USD" : `${arena}/USD`;
  const num = (p: number) => fmtPrice(arena, p).replace(/\.\d+$/, "");

  return (
    <>
      <div className="screen">
  <div className="chartRoot">
    <div className="orbL" aria-hidden="true" style={{ top: "-60px", left: "-45px", width: "480px", height: "480px" }}></div>
    <div className="pad" style={{ flex: "1", minHeight: "0", display: "flex", flexDirection: "column", paddingBottom: "8px" }}>
      {/* Telegram's own Back and menu buttons sit either side of this row, so only the title goes here. */}
      <div className="top" style={{ justifyContent: "center" }}>
        <span style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: "0" }}><span aria-hidden="true" style={{ width: "22px", height: "22px", flex: "none", borderRadius: "50%", background: COIN[arena] ?? "#8B949E", color: "#0B0E14", fontWeight: "800", fontSize: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}>{arena === "XAU" ? "G" : arena[0]}</span><span className="syne" style={{ fontWeight: "800", fontSize: "15px", whiteSpace: "nowrap" }}>{pair}</span></span>
      </div>

      <div style={{ marginTop: fit(4, 10), display: "flex", alignItems: "baseline", gap: "10px", whiteSpace: "nowrap" }}>
        <span className="mono" style={{ fontWeight: "800", fontSize: fit(26, 34) }}>{fmtPrice(arena, live)}</span>
        {change !== null && <span className="mono" style={{ fontSize: "14px", fontWeight: "700", color: change >= 0 ? "#CCFF00" : "#FF3366" }}>{change >= 0 ? "+" : ""}{change.toFixed(2)}%</span>}
        {change !== null && <span className="muted" style={{ fontSize: "12px" }}>1h</span>}
        <span className="chip chipL" style={{ height: "22px", fontSize: "10px", gap: "6px", marginLeft: "auto", alignSelf: "center" }}><span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#CCFF00" }}></span>LIVE</span>
      </div>
      <span className="muted tallOnly" style={{ fontSize: "11px", lineHeight: "15px" }}>Our battle price: the average of 4 exchanges. Battles settle on this exact line.</span>

      <div style={{ marginTop: fit(6, 12) }}><Tabs tf={tf} set={setTf} /></div>

      <div className="g" style={{ marginTop: "10px", borderRadius: "18px", position: "relative", flex: "1 0 150px", overflow: "hidden" }}>
        <CandleChart arena={arena} tf={tf} interactive={false} decimals={DECIMALS[arena] ?? 2} />
        <button onClick={() => go("App77")} aria-label="Full screen chart" style={{ position: "absolute", top: "10px", left: "12px", zIndex: "3", width: "32px", height: "32px", borderRadius: "10px", background: "rgba(11,14,20,0.8)", border: "1px solid rgba(255,255,255,0.14)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }} aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"></path></svg></button>
      </div>

      <div style={{ marginTop: "10px", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "8px" }}>
        <div className="g card" style={{ padding: "10px 12px" }}><span className="lbl" style={{ fontSize: "10px" }}>1H HIGH</span><div className="mono" style={{ fontWeight: "800", fontSize: "14px", marginTop: "3px" }}>{stats ? num(stats.high) : "--"}</div></div>
        <div className="g card" style={{ padding: "10px 12px" }}><span className="lbl" style={{ fontSize: "10px" }}>1H LOW</span><div className="mono" style={{ fontWeight: "800", fontSize: "14px", marginTop: "3px" }}>{stats ? num(stats.low) : "--"}</div></div>
        <div className="g card" style={{ padding: "10px 12px" }}><span className="lbl" style={{ fontSize: "10px", whiteSpace: "nowrap" }}>{minutes}-MIN MOVE</span><div className="mono" style={{ fontWeight: "800", fontSize: "14px", marginTop: "3px" }}>{stats ? `±${(stats.move * 100).toFixed(2)}%` : "--"}</div></div>
      </div>
    </div>

    <div style={{ flex: "none", padding: "10px 20px calc(var(--dd-nav-bottom) + 6px)", display: "flex", flexDirection: "column", gap: "10px" }}>
      {error && ERRORS[error] && (
        <div role="alert" style={{ padding: "10px 14px", borderRadius: "14px", background: "rgba(255,51,102,0.1)", border: "1px solid rgba(255,51,102,0.35)", color: "#FF5C85", fontSize: "13px", lineHeight: "18px" }}>
          {ERRORS[error]}{error === "low_balance" && <> <button onClick={() => void topUp()} style={{ font: "inherit", border: "0", background: "transparent", color: "#FFFFFF", textDecoration: "underline", cursor: "pointer", padding: "0" }}>Top up</button></>}
        </div>
      )}
      {open && <div className="row"><span className="muted" style={{ fontSize: "12px" }}>{minutes} MIN · <span className="mono" style={{ color: "#FFFFFF" }}>${selection.stake}</span> stake · <span className="lime mono">win +{payoutFor(selection.stake, feeNow()).payout}</span></span><button onClick={() => go("App09", true)} className="muted" style={{ fontSize: "12px", background: "none", border: "0", padding: "0", textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }}>Change</button></div>}
      {open ? <CallButtons height="56px" onPick={(s) => void pick(s)} disabled={busy} /> : <ComingSoon />}
    </div>
  </div>
</div>
    </>
  );
}
